import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Trash } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const STATUS_OPTIONS = ["scheduled", "in_progress", "delivery_pending", "on_hold", "at_risk", "delayed", "completed"];

const STATUS_LABELS = {
  scheduled:        "Scheduled",
  in_progress:      "In-Progress",
  delivery_pending: "Delivery Pending",
  on_hold:          "On-Hold",
  at_risk:          "At-Risk",
  delayed:          "Delayed",
  completed:        "Completed",
};
const STATUS_TONES = {
  scheduled:        "bg-zinc-100 text-zinc-800 border-zinc-300",
  in_progress:      "bg-blue-100 text-blue-800 border-blue-300",
  delivery_pending: "bg-indigo-100 text-indigo-800 border-indigo-300",
  on_hold:          "bg-amber-100 text-amber-800 border-amber-300",
  at_risk:          "bg-orange-100 text-orange-800 border-orange-300",
  delayed:          "bg-red-100 text-red-800 border-red-300",
  completed:        "bg-emerald-100 text-emerald-800 border-emerald-300",
};
const statusLabel = (s) => STATUS_LABELS[s] || (s || "scheduled").replace(/_/g, " ");
const statusTone  = (s) => STATUS_TONES[s] || STATUS_TONES.scheduled;

const MEDIA_LABELS = {
  Auto: "Total Autos",
  Cab: "Total Cabs",
  Bus: "Total Buses",
  Truck: "Total Trucks",
};
const targetLabel = (media) => MEDIA_LABELS[media] || "Total Vehicles";

export default function Campaigns() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ city: "all", status: "all", health: "all", q: "" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const remove = async (cid) => {
    if (!confirm(`Delete campaign ${cid}? This also removes its executions, planning slots and calendar entries.`)) return;
    try {
      await api.delete(`/campaigns/${cid}`);
      toast.success("Campaign deleted");
      fetchData();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeStatus = async (cid, newStatus) => {
    // Optimistic update
    setItems((prev) => prev.map((c) => c.campaign_id === cid ? { ...c, campaign_status: newStatus } : c));
    try {
      await api.patch(`/campaigns/${cid}`, { campaign_status: newStatus });
      toast.success(`Status set to ${statusLabel(newStatus)}`);
    } catch (e) {
      toast.error(formatApiError(e));
      fetchData(); // re-sync on failure
    }
  };

  const toggleSelect = (cid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map((c) => c.campaign_id)));
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!confirm(`Delete ${ids.length} campaigns? This will also remove all linked executions, planning slots and calendar entries. This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/campaigns/bulk-delete", { campaign_ids: ids });
      toast.success(`Deleted ${data.deleted} campaigns`);
      setSelectedIds(new Set());
      fetchData();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkDeleting(false); }
  };

  const canBulkDelete = user && (user.role === "admin" || user.role === "leadership");

  const fetchData = async () => {
    setLoading(true);
    const params = {};
    if (filters.city !== "all") params.city = filters.city;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.health !== "all") params.health = filters.health;
    if (filters.q) params.q = filters.q;
    try {
      const r = await api.get("/campaigns", { params });
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    api.get("/masters/cities").then((r) => setCities(r.data));
    api.get("/contacts", { params: { active: true } }).then((r) => setContacts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchData, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const canCreate = user && (user.role === "admin" || user.role === "command_center");

  return (
    <div className="space-y-5" data-testid="campaigns-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Control Center</p>
          <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Campaigns</h2>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2">
            {canBulkDelete && selectedIds.size > 0 && (
              <Button
                onClick={bulkDelete}
                disabled={bulkDeleting}
                variant="destructive"
                data-testid="bulk-delete-button"
              >
                <Trash className="h-4 w-4 mr-1.5" />
                {bulkDeleting ? "Deleting…" : `Delete ${selectedIds.size}`}
              </Button>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-campaign-button" className="bg-zinc-950 hover:bg-zinc-800 text-white">
                  <Plus className="h-4 w-4 mr-1.5" /> New Campaign
                </Button>
              </DialogTrigger>
              <NewCampaignDialog cities={cities} contacts={contacts} onClose={() => setOpen(false)} onCreated={fetchData} />
            </Dialog>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card className="p-4" data-testid="campaigns-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative lg:col-span-2">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
            <Input
              placeholder="Search ID, name, client…"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="pl-9"
              data-testid="campaigns-search-input"
            />
          </div>
          <Select value={filters.city} onValueChange={(v) => setFilters({ ...filters, city: v })}>
            <SelectTrigger data-testid="filter-city"><SelectValue placeholder="All cities" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cities</SelectItem>
              {cities.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="filter-status"><SelectValue placeholder="All status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.health} onValueChange={(v) => setFilters({ ...filters, health: v })}>
            <SelectTrigger data-testid="filter-health"><SelectValue placeholder="All health" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All health</SelectItem>
              <SelectItem value="green">Green — On track</SelectItem>
              <SelectItem value="amber">Amber — At risk</SelectItem>
              <SelectItem value="red">Red — Delayed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden" data-testid="campaigns-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b border-zinc-200">
              <tr>
                {canBulkDelete && (
                  <th className="text-left py-2.5 px-3 w-10">
                    <Checkbox
                      checked={items.length > 0 && selectedIds.size === items.length}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-campaigns"
                    />
                  </th>
                )}
                <th className="text-left py-2.5 px-4 font-semibold">Campaign ID</th>
                <th className="text-left py-2.5 px-4 font-semibold">Name</th>
                <th className="text-left py-2.5 px-4 font-semibold">Client</th>
                <th className="text-left py-2.5 px-4 font-semibold">City</th>
                <th className="text-left py-2.5 px-4 font-semibold">Pod</th>
                <th className="text-left py-2.5 px-4 font-semibold">ADO</th>
                <th className="text-left py-2.5 px-4 font-semibold">Progress</th>
                <th className="text-left py-2.5 px-4 font-semibold">Status</th>
                <th className="text-left py-2.5 px-4 font-semibold">Dates</th>
                <th className="text-left py-2.5 px-4 font-semibold">Health</th>
                <th className="text-left py-2.5 px-4 font-semibold w-12"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={canBulkDelete ? 12 : 11} className="text-center py-12 text-zinc-500 text-xs">Loading campaigns…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={canBulkDelete ? 12 : 11} className="text-center py-12 text-zinc-500 text-xs">No campaigns match your filters.</td></tr>
              )}
              {items.map((c) => {
                const pct = c.target_installations ? Math.round((c.completed_installations / c.target_installations) * 100) : 0;
                return (
                  <tr key={c.campaign_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`campaign-row-${c.campaign_id}`}>
                    <td className="py-2.5 px-4 font-mono text-xs">
                      <Link to={`/campaigns/${c.campaign_id}`} className="text-zinc-950 hover:underline font-semibold" data-testid={`campaign-link-${c.campaign_id}`}>
                        {c.campaign_id}
                      </Link>
                    </td>
                    <td className="py-2.5 px-4 font-medium">{c.campaign_name}</td>
                    <td className="py-2.5 px-4 text-zinc-600">{c.client_name}</td>
                    <td className="py-2.5 px-4 text-zinc-600">{c.city}</td>
                    <td className="py-2.5 px-4 text-zinc-600">{c.pod || "—"}</td>
                    <td className="py-2.5 px-4 text-zinc-600">{c.ado_name || "—"}</td>
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                          <div className={`h-full ${c.health === "red" ? "bg-red-500" : c.health === "amber" ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                        </div>
                        <span className="text-xs font-mono text-zinc-600">{c.completed_installations}/{c.target_installations}</span>
                      </div>
                    </td>
                    <td className="py-2.5 px-4">
                      {canCreate ? (
                        <Select
                          value={c.campaign_status || "scheduled"}
                          onValueChange={(v) => changeStatus(c.campaign_id, v)}
                        >
                          <SelectTrigger
                            className={`h-7 px-2 text-xs border ${statusTone(c.campaign_status)} font-medium w-[150px]`}
                            data-testid={`status-trigger-${c.campaign_id}`}
                          >
                            <SelectValue>{statusLabel(c.campaign_status)}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s} value={s} data-testid={`status-option-${c.campaign_id}-${s}`}>
                                {statusLabel(s)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${statusTone(c.campaign_status)}`}>
                          {statusLabel(c.campaign_status)}
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-4 text-xs text-zinc-500 font-mono">{c.start_date} → {c.end_date}</td>
                    <td className="py-2.5 px-4"><StatusPill status={c.health} /></td>
                    <td className="py-2.5 px-4">
                      {canCreate && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 hover:bg-zinc-100 rounded" data-testid={`row-actions-${c.campaign_id}`}>
                              <MoreHorizontal className="h-4 w-4 text-zinc-500" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditing(c)} data-testid={`edit-${c.campaign_id}`}>
                              <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => remove(c.campaign_id)} className="text-red-600" data-testid={`delete-${c.campaign_id}`}>
                              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && (
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <EditCampaignDialog campaign={editing} cities={cities} contacts={contacts} onClose={() => setEditing(null)} onSaved={fetchData} />
        </Dialog>
      )}
    </div>
  );
}

function EditCampaignDialog({ campaign, cities, contacts = [], onClose, onSaved }) {
  const [form, setForm] = useState({
    campaign_name: campaign.campaign_name || "",
    client_name: campaign.client_name || "",
    media_type: campaign.media_type || "Auto",
    city: campaign.city || "",
    start_date: campaign.start_date || "",
    end_date: campaign.end_date || "",
    target_installations: campaign.target_installations || 0,
    target_t: campaign.target_t ?? 0,
    target_nt: campaign.target_nt ?? 0,
    target_d: campaign.target_d ?? 0,
    priority: campaign.priority || "medium",
    ado_name: campaign.ado_name || "",
    pod_lead: campaign.pod_lead || "",
    printing_vendor: campaign.printing_vendor || "",
    fleet_vendor: campaign.fleet_vendor || "",
    campaign_status: campaign.campaign_status || "scheduled",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/campaigns/${campaign.campaign_id}`, {
        ...form,
        target_installations: Number(form.target_installations),
        target_t: Number(form.target_t) || 0,
        target_nt: Number(form.target_nt) || 0,
        target_d: Number(form.target_d) || 0,
      });
      toast.success("Campaign updated");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const breakupSum = (Number(form.target_t)||0) + (Number(form.target_nt)||0) + (Number(form.target_d)||0);
  const totalNum = Number(form.target_installations)||0;
  return (
    <DialogContent className="max-w-2xl" data-testid="edit-campaign-dialog">
      <DialogHeader><DialogTitle className="font-display">Edit campaign · <span className="font-mono text-sm text-zinc-500">{campaign.campaign_id}</span></DialogTitle></DialogHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
        <div className="sm:col-span-2"><Label className="text-xs">Campaign Name</Label><Input value={form.campaign_name} onChange={(e) => setForm({ ...form, campaign_name: e.target.value })} data-testid="edit-name" /></div>
        <div><Label className="text-xs">Client</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="edit-client" /></div>
        <div>
          <Label className="text-xs">Media Type</Label>
          <Select value={form.media_type} onValueChange={(v) => setForm({ ...form, media_type: v })}>
            <SelectTrigger data-testid="edit-media"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Auto">Auto</SelectItem><SelectItem value="Cab">Cab</SelectItem>
              <SelectItem value="Bus">Bus</SelectItem><SelectItem value="Truck">Truck</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">City</Label>
          <Input
            list="edit-cities-suggest"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            placeholder="Pick or type a city"
            data-testid="edit-city"
          />
          <datalist id="edit-cities-suggest">
            {cities.filter((c) => c.name).map((c) => <option key={c.id} value={c.name} />)}
          </datalist>
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={form.campaign_status} onValueChange={(v) => setForm({ ...form, campaign_status: v })}>
            <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) =>
                <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">{targetLabel(form.media_type)}</Label><Input type="number" value={form.target_installations} onChange={(e) => setForm({ ...form, target_installations: e.target.value })} data-testid="edit-target" /></div>
        <div className="sm:col-span-2 grid grid-cols-3 gap-2 p-2 bg-zinc-50 border border-zinc-200 rounded-md">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Total T</Label>
            <Input type="number" min="0" value={form.target_t} onChange={(e) => setForm({ ...form, target_t: e.target.value })} data-testid="edit-target-t" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Total N.T</Label>
            <Input type="number" min="0" value={form.target_nt} onChange={(e) => setForm({ ...form, target_nt: e.target.value })} data-testid="edit-target-nt" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Total D</Label>
            <Input type="number" min="0" value={form.target_d} onChange={(e) => setForm({ ...form, target_d: e.target.value })} data-testid="edit-target-d" />
          </div>
          <div className="col-span-3 text-[10px] text-zinc-500 font-mono">
            T+N.T+D = <span className={`font-semibold ${breakupSum !== totalNum && breakupSum > 0 ? "text-amber-700" : "text-zinc-700"}`}>{breakupSum}</span> / {totalNum}
          </div>
        </div>
        <div><Label className="text-xs">Printing Vendor</Label><Input value={form.printing_vendor} onChange={(e) => setForm({ ...form, printing_vendor: e.target.value })} data-testid="edit-printing-vendor" /></div>
        <div><Label className="text-xs">Fleet Vendor</Label><Input value={form.fleet_vendor} onChange={(e) => setForm({ ...form, fleet_vendor: e.target.value })} data-testid="edit-fleet-vendor" /></div>
        <div><Label className="text-xs">Start</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="edit-start" /></div>
        <div><Label className="text-xs">End</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="edit-end" /></div>
        <div><Label className="text-xs">ADO</Label>
          <Input list="edit-ado-suggest" value={form.ado_name} onChange={(e) => setForm({ ...form, ado_name: e.target.value })} data-testid="edit-ado" />
          <datalist id="edit-ado-suggest">
            {contacts.filter((c) => c.role === "ado").map((c) => <option key={c.id} value={c.name}>{c.email}</option>)}
          </datalist>
        </div>
        <div><Label className="text-xs">Pod Lead</Label>
          <Input list="edit-pod-lead-suggest" value={form.pod_lead} onChange={(e) => setForm({ ...form, pod_lead: e.target.value })} data-testid="edit-pod-lead" />
          <datalist id="edit-pod-lead-suggest">
            {contacts.filter((c) => c.role === "pod_lead").map((c) => <option key={c.id} value={c.name}>{c.email}</option>)}
          </datalist>
        </div>
      </div>
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onClose} data-testid="edit-cancel">Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="edit-submit">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function NewCampaignDialog({ cities, contacts = [], onClose, onCreated }) {
  const [form, setForm] = useState({
    campaign_name: "",
    client_name: "",
    media_type: "Auto",
    city: cities[0]?.name || "",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    target_installations: 50,
    target_t: 0,
    target_nt: 0,
    target_d: 0,
    priority: "medium",
    ado_name: "",
    pod_lead: "",
    printing_vendor: "",
    fleet_vendor: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.city.trim()) { toast.error("Pick or type a city"); return; }
    setSubmitting(true);
    try {
      await api.post("/campaigns", {
        ...form,
        city: form.city.trim(),
        target_installations: Number(form.target_installations),
        target_t: Number(form.target_t) || 0,
        target_nt: Number(form.target_nt) || 0,
        target_d: Number(form.target_d) || 0,
      });
      toast.success("Campaign created");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const isNewCity = form.city && !cities.some((c) => c.name.toLowerCase() === form.city.trim().toLowerCase());
  const breakupSum = (Number(form.target_t)||0) + (Number(form.target_nt)||0) + (Number(form.target_d)||0);
  const totalNum = Number(form.target_installations)||0;

  return (
    <DialogContent className="max-w-2xl" data-testid="new-campaign-dialog">
      <DialogHeader>
        <DialogTitle className="font-display">Create new campaign</DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
        <div className="sm:col-span-2">
          <Label className="text-xs">Campaign Name</Label>
          <Input value={form.campaign_name} onChange={(e) => setForm({ ...form, campaign_name: e.target.value })} data-testid="new-campaign-name" />
        </div>
        <div>
          <Label className="text-xs">Client</Label>
          <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="new-campaign-client" />
        </div>
        <div>
          <Label className="text-xs">Media Type</Label>
          <Select value={form.media_type} onValueChange={(v) => setForm({ ...form, media_type: v })}>
            <SelectTrigger data-testid="new-campaign-media"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Auto">Auto</SelectItem>
              <SelectItem value="Cab">Cab</SelectItem>
              <SelectItem value="Bus">Bus</SelectItem>
              <SelectItem value="Truck">Truck</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">City <span className="text-zinc-400 font-normal">(pick or type a new one)</span></Label>
          <Input
            list="cities-suggest"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            placeholder="Bangalore / Mysore / …"
            data-testid="new-campaign-city"
          />
          <datalist id="cities-suggest">
            {cities.filter((c) => c.name).map((c) => <option key={c.id} value={c.name} />)}
          </datalist>
          {isNewCity && (
            <p className="text-[10px] text-amber-700 mt-1" data-testid="new-city-hint">
              ✦ "{form.city}" will be added to the master city list automatically.
            </p>
          )}
        </div>
        <div>
          <Label className="text-xs">Priority</Label>
          <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
            <SelectTrigger data-testid="new-campaign-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">{targetLabel(form.media_type)} <span className="text-zinc-400 font-normal">(lifecycle starts here)</span></Label>
          <Input type="number" min="1" value={form.target_installations} onChange={(e) => setForm({ ...form, target_installations: e.target.value })} data-testid="new-campaign-target" />
        </div>
        <div className="sm:col-span-2 grid grid-cols-3 gap-2 p-3 bg-zinc-50 border border-zinc-200 rounded-md">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Total T <span className="text-zinc-400 normal-case">(Trusted)</span></Label>
            <Input type="number" min="0" value={form.target_t} onChange={(e) => setForm({ ...form, target_t: e.target.value })} data-testid="new-target-t" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Total N.T <span className="text-zinc-400 normal-case">(Non-Trusted)</span></Label>
            <Input type="number" min="0" value={form.target_nt} onChange={(e) => setForm({ ...form, target_nt: e.target.value })} data-testid="new-target-nt" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Total D <span className="text-zinc-400 normal-case">(Delivery)</span></Label>
            <Input type="number" min="0" value={form.target_d} onChange={(e) => setForm({ ...form, target_d: e.target.value })} data-testid="new-target-d" />
          </div>
          <div className="col-span-3 text-[10px] text-zinc-500 font-mono" data-testid="new-target-breakup-hint">
            T + N.T + D = <span className={`font-semibold ${breakupSum !== totalNum && breakupSum > 0 ? "text-amber-700" : "text-emerald-700"}`}>{breakupSum}</span>
            <span className="text-zinc-400"> / {totalNum} {targetLabel(form.media_type).toLowerCase()}</span>
            {breakupSum > 0 && breakupSum !== totalNum && <span className="text-amber-700 ml-2">⚠ breakup doesn't match total</span>}
          </div>
        </div>
        <div>
          <Label className="text-xs">Printing Vendor</Label>
          <Input value={form.printing_vendor} onChange={(e) => setForm({ ...form, printing_vendor: e.target.value })} placeholder="e.g. Vista Prints" data-testid="new-printing-vendor" />
        </div>
        <div>
          <Label className="text-xs">Fleet Vendor</Label>
          <Input value={form.fleet_vendor} onChange={(e) => setForm({ ...form, fleet_vendor: e.target.value })} placeholder="e.g. Ola Cabs / Rapido" data-testid="new-fleet-vendor" />
        </div>
        <div>
          <Label className="text-xs">Start Date</Label>
          <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="new-campaign-start" />
        </div>
        <div>
          <Label className="text-xs">End Date</Label>
          <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="new-campaign-end" />
        </div>
        <div>
          <Label className="text-xs">ADO</Label>
          <Input
            list="ado-suggest"
            value={form.ado_name}
            onChange={(e) => setForm({ ...form, ado_name: e.target.value })}
            placeholder="Pick or type ADO name"
            data-testid="new-campaign-ado"
          />
          <datalist id="ado-suggest">
            {contacts.filter((c) => c.role === "ado").map((c) => <option key={c.id} value={c.name}>{c.email}</option>)}
          </datalist>
        </div>
        <div>
          <Label className="text-xs">Pod Lead</Label>
          <Input
            list="pod-lead-suggest"
            value={form.pod_lead}
            onChange={(e) => setForm({ ...form, pod_lead: e.target.value })}
            placeholder="Pick or type pod lead name"
            data-testid="new-campaign-pod-lead"
          />
          <datalist id="pod-lead-suggest">
            {contacts.filter((c) => c.role === "pod_lead").map((c) => <option key={c.id} value={c.name}>{c.email}</option>)}
          </datalist>
        </div>
      </div>
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onClose} data-testid="new-campaign-cancel">Cancel</Button>
        <Button onClick={submit} disabled={submitting} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="new-campaign-submit">
          {submitting ? "Creating…" : "Create Campaign"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
